import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "./auth";
import { useRouter } from "./router";
import {
  ADMIN_MODULE_OPTIONS,
  REAL_MODULE_KEYS,
  filterRealModuleKeys,
  getModuleLabel,
  isRealModuleKey,
} from "./modules";
import {
  createAdminUserMembership,
  createInvite,
  deleteAdminUser,
  getAdminUserDetail,
  listAdminUsers,
  listAccessRequests,
  listElectionsCatalog,
  updateAdminUser,
  updateAdminUserMembership,
  updateAccessRequest,
} from "./adminApi";
import { sortElectionsNewestFirst } from "./electionOrdering";

const REQUEST_STATUSES = ["new", "contacted", "approved", "rejected", "provisioned"];
const MODULE_OPTIONS = ADMIN_MODULE_OPTIONS;
const INVITE_ROLES = ["viewer", "editor", "admin"];
const REQUEST_TABLE_COLUMNS = [
  { width: "96px" },
  { width: "196px" },
  { width: "148px" },
  { width: "112px" },
  { width: "188px" },
  { width: "104px" },
  { width: "116px" },
  { width: "132px" },
  { width: "90px" },
  { width: "132px" },
  { width: "90px" },
];

const adminStyles = {
  container: {
    width: "100%",
    maxWidth: "100%",
    margin: 0,
    padding: "22px 0 40px",
    display: "grid",
    gap: 12,
  },
  card: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "white",
    padding: 16,
    display: "grid",
    gap: 12,
  },
  controlsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  select: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 14,
    background: "white",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "9px 10px",
    fontSize: 14,
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    resize: "vertical",
    minHeight: 48,
  },
  button: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 10px",
    background: "white",
    cursor: "pointer",
    fontSize: 13,
  },
  primaryBtn: {
    border: "1px solid #0f172a",
    borderRadius: 8,
    padding: "9px 11px",
    background: "#0f172a",
    color: "white",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  dangerBtn: {
    border: "1px solid #b91c1c",
    borderRadius: 8,
    padding: "9px 11px",
    background: "#b91c1c",
    color: "white",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  tabBtn: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "8px 12px",
    background: "white",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  tabBtnActive: {
    background: "#0f172a",
    borderColor: "#0f172a",
    color: "white",
  },
  error: {
    border: "1px solid #fecaca",
    borderRadius: 8,
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    fontSize: 13,
  },
  message: {
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    background: "#eff6ff",
    color: "#1e3a8a",
    padding: "8px 10px",
    fontSize: 13,
  },
  warning: {
    border: "1px solid #facc15",
    borderRadius: 8,
    background: "#fefce8",
    color: "#854d0e",
    padding: "8px 10px",
    fontSize: 13,
  },
  summaryGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  },
  tableWrapper: {
    overflowX: "auto",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    minWidth: 1080,
    tableLayout: "fixed",
  },
  tableHeaderCell: {
    padding: "8px 5px",
    textAlign: "left",
    verticalAlign: "top",
    fontSize: 12,
    color: "#334155",
    whiteSpace: "normal",
    lineHeight: 1.35,
  },
  tableCell: {
    padding: "8px 5px",
    verticalAlign: "top",
    fontSize: 12,
    color: "#0f172a",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.4,
  },
  rowActionSelect: {
    width: "100%",
    minWidth: 0,
    padding: "7px 8px",
    fontSize: 12,
  },
  rowButton: {
    width: "100%",
    padding: "7px 8px",
    fontSize: 12,
  },
  saveButton: {
    border: "1px solid #0f172a",
    borderRadius: 8,
    padding: "7px 8px",
    background: "#0f172a",
    color: "white",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    width: "100%",
  },
  saveButtonDisabled: {
    background: "#f8fafc",
    color: "#94a3b8",
    borderColor: "#cbd5e1",
    cursor: "not-allowed",
  },
  inlineFeedback: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 1.35,
  },
  summaryStat: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    background: "#f8fafc",
    padding: 12,
    display: "grid",
    gap: 6,
  },
  summaryLabel: {
    fontSize: 12,
    color: "#475569",
  },
  summaryValue: {
    fontSize: 24,
    fontWeight: 700,
    color: "#0f172a",
  },
  detailsPanel: {
    padding: 16,
    background: "#f8fafc",
    borderRadius: 10,
    display: "grid",
    gap: 14,
  },
  detailsGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  },
  detailsSection: {
    display: "grid",
    gap: 8,
    alignContent: "start",
  },
  splitPanel: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "minmax(280px, 360px) minmax(0, 1fr)",
    alignItems: "start",
  },
  splitPanelMobile: {
    display: "grid",
    gap: 16,
  },
  sidePanel: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#f8fafc",
    padding: 12,
    display: "grid",
    gap: 10,
    alignContent: "start",
  },
  sideList: {
    display: "grid",
    gap: 8,
    maxHeight: 900,
    overflowY: "auto",
  },
  listItemButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    background: "white",
    padding: 12,
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gap: 6,
  },
  listItemButtonActive: {
    borderColor: "#0f172a",
    boxShadow: "inset 0 0 0 1px #0f172a",
  },
  listItemTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    wordBreak: "break-word",
  },
  listItemMeta: {
    fontSize: 12,
    color: "#475569",
    lineHeight: 1.45,
    wordBreak: "break-word",
  },
  badgeRow: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 700,
    color: "#334155",
    background: "#ffffff",
  },
  compareGrid: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  },
  compareCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    background: "#ffffff",
    padding: 10,
    display: "grid",
    gap: 8,
  },
  membershipCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#ffffff",
    padding: 14,
    display: "grid",
    gap: 12,
  },
  fieldGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  requestCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#ffffff",
    padding: 14,
    display: "grid",
    gap: 12,
  },
  detailsLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  detailsText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 13,
    lineHeight: 1.5,
    color: "#0f172a",
  },
  wrappedList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  wrappedListItem: {
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    padding: "6px 10px",
    background: "white",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  pre: {
    margin: 0,
    padding: 12,
    borderRadius: 8,
    background: "#0f172a",
    color: "#e2e8f0",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowX: "auto",
  },
  stackedDateTime: {
    display: "grid",
    gap: 2,
    lineHeight: 1.25,
  },
  stackedDatePrimary: {
    whiteSpace: "nowrap",
  },
  stackedDateSecondary: {
    color: "#475569",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
};

function toPositiveIntOrNull(value) {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
}

function formatDateTimeParts(input) {
  if (!input) return { dateLabel: "", timeLabel: "" };
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return { dateLabel: String(input), timeLabel: "" };
  }
  return {
    dateLabel: date.toLocaleDateString("cs-CZ"),
    timeLabel: date.toLocaleTimeString("cs-CZ", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
}

function getRequestPayloadJson(row) {
  return row && isPlainObject(row.payload_json) ? row.payload_json : null;
}

function getRequestPriceTotalKc(payloadJson) {
  const value = payloadJson && payloadJson.price_total_kc;
  return Number.isInteger(value) && value >= 0 ? value : null;
}

function formatPriceKc(value) {
  if (!Number.isInteger(value) || value < 0) return "—";
  return `${new Intl.NumberFormat("cs-CZ").format(value)} Kč`;
}

function getRequestModuleLabel(moduleKey) {
  return getModuleLabel(moduleKey);
}

function getCompactModulesLabel(items) {
  if (!Array.isArray(items) || items.length === 0) return "—";
  if (items.length === 1) return getRequestModuleLabel(items[0]);
  return `${items.length} modules`;
}

function getBreakdownEntries(priceBreakdown) {
  if (!isPlainObject(priceBreakdown)) return [];
  return Object.entries(priceBreakdown).filter(([, value]) => value !== undefined && value !== null);
}

function formatBreakdownValue(groupKey, itemKey, amount) {
  const label = groupKey === "modules" ? getRequestModuleLabel(itemKey) : itemKey;
  return `${label}: ${formatPriceKc(amount)}`;
}

async function copyText(text) {
  const value = String(text || "");
  if (!value) return;

  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    await navigator.clipboard.writeText(value);
    return;
  }

  const el = document.createElement("textarea");
  el.value = value;
  el.setAttribute("readonly", "");
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
}

function getErrorMessage(error) {
  if (error && typeof error.message === "string" && error.message) return error.message;
  return "Request failed";
}

function normalizeModuleKeysFromMap(modulesMap) {
  if (!isPlainObject(modulesMap)) return [];
  return Object.entries(modulesMap)
    .filter(([key, value]) => value === true && isRealModuleKey(key))
    .map(([key]) => String(key))
    .sort((a, b) => a.localeCompare(b, "cs"));
}

function sanitizeModulesMap(modulesMap) {
  if (!isPlainObject(modulesMap)) return {};
  return REAL_MODULE_KEYS.reduce((next, key) => {
    if (modulesMap[key] === true) next[key] = true;
    return next;
  }, {});
}

function buildMembershipDraft(membership) {
  return {
    enabledElectionIds: normalizeStringList(membership && membership.enabled_elections),
    modules: isPlainObject(membership && membership.modules) ? { ...membership.modules } : {},
    role: membership && membership.role ? String(membership.role) : "viewer",
    isActive: Boolean(membership && membership.is_active),
    pendingModuleKey: "",
    isSaving: false,
    error: "",
    successMessage: "",
  };
}

function buildNewMembershipDraft() {
  return {
    ...buildMembershipDraft(null),
    isActive: true,
  };
}

function diffStringLists(requestedItems, enabledItems) {
  const requested = Array.from(new Set(normalizeStringList(requestedItems)));
  const enabled = Array.from(new Set(normalizeStringList(enabledItems)));
  const requestedSet = new Set(requested);
  const enabledSet = new Set(enabled);

  return {
    shared: requested.filter((item) => enabledSet.has(item)),
    requestedOnly: requested.filter((item) => !enabledSet.has(item)),
    enabledOnly: enabled.filter((item) => !requestedSet.has(item)),
  };
}

function buildModuleKeys(...sources) {
  const keys = Array.from(
    new Set(
      sources.flatMap((source) => {
        if (Array.isArray(source)) return source;
        if (isPlainObject(source)) return Object.keys(source);
        return [];
      })
    )
  );
  return filterRealModuleKeys(keys).sort((a, b) => a.localeCompare(b, "cs"));
}

async function pushRequestToPipedriveStub(requestId, areaId) {
  const response = await fetch(`/api/admin/pipedrive/push-request/${encodeURIComponent(String(requestId))}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      area_id: areaId,
    }),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (_) {
    payload = null;
  }

  if (!response.ok) {
    const message =
      payload && typeof payload.error === "string" && payload.error
        ? payload.error
        : `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export default function AdminPage({ initialTab = "requests" }) {
  const auth = useAuth();
  const { navigate } = useRouter();

  const isSuperadmin = Boolean(auth.user && auth.user.is_superadmin);
  const activeTab =
    initialTab === "users" && isSuperadmin
      ? "users"
      : initialTab === "invites"
        ? "invites"
        : "requests";

  const areaOptions = useMemo(() => {
    const dedup = new Map();
    (auth.availableAreas || []).forEach((area) => {
      if (!area) return;
      const areaId = toPositiveIntOrNull(area.id);
      if (!areaId) return;
      if (dedup.has(areaId)) return;
      dedup.set(areaId, {
        id: areaId,
        name: area.name ? String(area.name) : `Area ${areaId}`,
      });
    });
    return Array.from(dedup.values()).sort((a, b) => a.id - b.id);
  }, [auth.availableAreas]);

  const preferredAreaId = useMemo(() => {
    const authDefault = toPositiveIntOrNull(auth.defaultAreaId);
    if (authDefault && areaOptions.some((opt) => opt.id === authDefault)) {
      return authDefault;
    }
    return areaOptions.length > 0 ? areaOptions[0].id : null;
  }, [auth.defaultAreaId, areaOptions]);

  const [selectedAreaId, setSelectedAreaId] = useState(preferredAreaId);
  const [adminApiForbidden, setAdminApiForbidden] = useState(false);

  useEffect(() => {
    setSelectedAreaId((prev) => {
      if (prev !== null && areaOptions.some((opt) => opt.id === prev)) return prev;
      return preferredAreaId;
    });
  }, [areaOptions, preferredAreaId]);

  const selectedMembership = useMemo(
    () =>
      (auth.memberships || []).find(
        (membership) =>
          membership &&
          Boolean(membership.is_active) === true &&
          Number(membership.area_id) === Number(selectedAreaId)
      ) || null,
    [auth.memberships, selectedAreaId]
  );

  const canAdminSelectedArea = Boolean(
    selectedAreaId && (isSuperadmin || (selectedMembership && selectedMembership.role === "admin"))
  );
  const shouldShowForbidden = Boolean(selectedAreaId && (!canAdminSelectedArea || adminApiForbidden));

  const [requestStatusFilter, setRequestStatusFilter] = useState("");
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState("");
  const [requestDrafts, setRequestDrafts] = useState({});
  const [expandedRequestId, setExpandedRequestId] = useState(null);

  const [electionsCatalog, setElectionsCatalog] = useState([]);
  const [electionsLoading, setElectionsLoading] = useState(false);
  const [electionsError, setElectionsError] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [enabledElectionIds, setEnabledElectionIds] = useState([]);
  const [modulesMap, setModulesMap] = useState({ trends: true });
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [copyMessage, setCopyMessage] = useState("");

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetailError, setUserDetailError] = useState("");
  const [membershipDrafts, setMembershipDrafts] = useState({});
  const [userActionPending, setUserActionPending] = useState("");
  const [userActionError, setUserActionError] = useState("");
  const [userActionMessage, setUserActionMessage] = useState("");
  const [newMembershipAreaId, setNewMembershipAreaId] = useState(null);
  const [newMembershipDraft, setNewMembershipDraft] = useState(() => buildNewMembershipDraft());
  const [newMembershipSubmitting, setNewMembershipSubmitting] = useState(false);
  const [newMembershipError, setNewMembershipError] = useState("");
  const [newMembershipMessage, setNewMembershipMessage] = useState("");
  const [newMembershipElectionsCatalog, setNewMembershipElectionsCatalog] = useState([]);
  const [newMembershipElectionsLoading, setNewMembershipElectionsLoading] = useState(false);
  const [newMembershipElectionsError, setNewMembershipElectionsError] = useState("");

  const handleApiError = useCallback(
    (error, setError) => {
      if (error && error.status === 401) {
        navigate("/app/login", { replace: true });
        return;
      }
      if (error && error.status === 403) {
        setAdminApiForbidden(true);
        if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
        setError("Forbidden");
        return;
      }
      setError(getErrorMessage(error));
    },
    [auth, navigate]
  );

  const loadRequests = useCallback(async () => {
    if (!selectedAreaId || !canAdminSelectedArea) {
      setRequests([]);
      setRequestsError("");
      setRequestsLoading(false);
      return;
    }

    setRequestsLoading(true);
    setRequestsError("");
    try {
      const rows = await listAccessRequests({
        areaId: selectedAreaId,
        status: requestStatusFilter || undefined,
        limit: 100,
      });
      setRequests(Array.isArray(rows) ? rows : []);
    } catch (error) {
      handleApiError(error, setRequestsError);
    } finally {
      setRequestsLoading(false);
    }
  }, [canAdminSelectedArea, handleApiError, requestStatusFilter, selectedAreaId]);

  const loadElectionsCatalog = useCallback(async () => {
    if (!selectedAreaId || !canAdminSelectedArea) {
      setElectionsCatalog([]);
      setElectionsError("");
      setElectionsLoading(false);
      return;
    }

    setElectionsLoading(true);
    setElectionsError("");
    try {
      const rows = await listElectionsCatalog({ areaId: selectedAreaId });
      const list = sortElectionsNewestFirst(Array.isArray(rows) ? rows : []);
      setElectionsCatalog(list);
      const allowedIds = new Set(list.map((row) => String(row.id || "")));
      setEnabledElectionIds((prev) => prev.filter((id) => allowedIds.has(String(id))));
    } catch (error) {
      handleApiError(error, setElectionsError);
    } finally {
      setElectionsLoading(false);
    }
  }, [canAdminSelectedArea, handleApiError, selectedAreaId]);

  const loadUsers = useCallback(async () => {
    if (!isSuperadmin) {
      setUsers([]);
      setUsersError("");
      setUsersLoading(false);
      return;
    }

    setUsersLoading(true);
    setUsersError("");
    try {
      const rows = await listAdminUsers();
      setUsers(Array.isArray(rows) ? rows : []);
    } catch (error) {
      handleApiError(error, setUsersError);
    } finally {
      setUsersLoading(false);
    }
  }, [handleApiError, isSuperadmin]);

  const loadUserDetail = useCallback(
    async (userId) => {
      const normalizedUserId = toPositiveIntOrNull(userId);
      if (!normalizedUserId || !isSuperadmin) {
        setUserDetail(null);
        setUserDetailError("");
        setUserDetailLoading(false);
        return;
      }

      setUserDetailLoading(true);
      setUserDetailError("");
      try {
        const payload = await getAdminUserDetail(normalizedUserId);
        setUserDetail(payload || null);
      } catch (error) {
        handleApiError(error, setUserDetailError);
      } finally {
        setUserDetailLoading(false);
      }
    },
    [handleApiError, isSuperadmin]
  );

  useEffect(() => {
    setAdminApiForbidden(false);
    setRequests([]);
    setRequestsError("");
    setExpandedRequestId(null);
    setElectionsCatalog([]);
    setElectionsError("");
    setInviteResult(null);
  }, [selectedAreaId]);

  useEffect(() => {
    if (activeTab !== "users") return;
    loadUsers();
  }, [activeTab, loadUsers]);

  useEffect(() => {
    if (activeTab !== "users") return;
    if (!selectedUserId) return;
    loadUserDetail(selectedUserId);
  }, [activeTab, loadUserDetail, selectedUserId]);

  useEffect(() => {
    if (activeTab !== "requests") return;
    loadRequests();
  }, [activeTab, loadRequests]);

  useEffect(() => {
    if (activeTab !== "invites") return;
    loadElectionsCatalog();
  }, [activeTab, loadElectionsCatalog]);

  useEffect(() => {
    const nextDrafts = {};
    requests.forEach((row) => {
      nextDrafts[row.id] = {
        status: row.status || "new",
        notes: row.notes || "",
        isSaving: false,
        isPipedriveSending: false,
        error: "",
        successMessage: "",
        pipedriveError: "",
        pipedriveMessage: "",
        copyMessage: "",
      };
    });
    setRequestDrafts(nextDrafts);
  }, [requests]);

  useEffect(() => {
    const nextDrafts = {};
    const memberships = Array.isArray(userDetail && userDetail.memberships) ? userDetail.memberships : [];
    memberships.forEach((membership) => {
      nextDrafts[membership.id] = buildMembershipDraft(membership);
    });
    setMembershipDrafts(nextDrafts);
    setNewMembershipDraft(buildNewMembershipDraft());
    setNewMembershipError("");
    setNewMembershipMessage("");
  }, [userDetail]);

  useEffect(() => {
    if (activeTab !== "users") return;
    setUserActionError("");
    setUserActionMessage("");
  }, [activeTab, selectedUserId]);

  const availableAreasForNewMembership = useMemo(() => {
    const detailAreas = Array.isArray(userDetail && userDetail.available_areas) ? userDetail.available_areas : [];
    const fallbackAreas = Array.isArray(auth.availableAreas) ? auth.availableAreas : [];
    const candidateAreas = detailAreas.length > 0 ? detailAreas : fallbackAreas;
    const usedAreaIds = new Set(
      (Array.isArray(userDetail && userDetail.memberships) ? userDetail.memberships : []).map((membership) =>
        Number(membership.area_id)
      )
    );
    return candidateAreas
      .map((area) => ({
        id: toPositiveIntOrNull(area && area.id),
        name: area && area.name ? String(area.name) : null,
      }))
      .filter((area) => area.id && !usedAreaIds.has(area.id))
      .sort((a, b) => a.id - b.id);
  }, [auth.availableAreas, userDetail]);

  useEffect(() => {
    if (activeTab !== "users") return;
    if (availableAreasForNewMembership.length === 0) {
      setNewMembershipAreaId(null);
      setNewMembershipElectionsCatalog([]);
      setNewMembershipElectionsError("");
      setNewMembershipElectionsLoading(false);
      return;
    }
    if (!availableAreasForNewMembership.some((area) => area.id === Number(newMembershipAreaId))) {
      setNewMembershipAreaId(availableAreasForNewMembership[0].id);
    }
  }, [activeTab, availableAreasForNewMembership, newMembershipAreaId]);

  useEffect(() => {
    if (activeTab !== "users" || !newMembershipAreaId) {
      setNewMembershipElectionsCatalog([]);
      setNewMembershipElectionsError("");
      setNewMembershipElectionsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setNewMembershipElectionsLoading(true);
      setNewMembershipElectionsError("");
      try {
        const rows = await listElectionsCatalog({ areaId: newMembershipAreaId });
        if (cancelled) return;
        const list = sortElectionsNewestFirst(Array.isArray(rows) ? rows : []);
        setNewMembershipElectionsCatalog(list);
        const allowedIds = new Set(list.map((row) => String(row.id || "")));
        setNewMembershipDraft((prev) => ({
          ...prev,
          enabledElectionIds: (prev.enabledElectionIds || []).filter((id) => allowedIds.has(String(id))),
        }));
      } catch (error) {
        if (cancelled) return;
        handleApiError(error, setNewMembershipElectionsError);
      } finally {
        if (!cancelled) {
          setNewMembershipElectionsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, handleApiError, newMembershipAreaId]);

  const filteredUsers = useMemo(() => {
    const query = String(userSearch || "").trim().toLowerCase();
    if (!query) return users;
    return users.filter((row) => {
      const haystack = [row && row.name, row && row.email]
        .map((value) => (value ? String(value).toLowerCase() : ""))
        .join(" ");
      return haystack.includes(query);
    });
  }, [userSearch, users]);

  useEffect(() => {
    if (activeTab !== "users") return;
    if (filteredUsers.length === 0) {
      setSelectedUserId(null);
      return;
    }
    if (!filteredUsers.some((user) => Number(user.id) === Number(selectedUserId))) {
      setSelectedUserId(filteredUsers[0].id);
    }
  }, [activeTab, filteredUsers, selectedUserId]);

  const requestsSummary = useMemo(() => {
    return requests.reduce(
      (acc, row) => {
        const payloadJson = getRequestPayloadJson(row);
        const priceTotalKc = getRequestPriceTotalKc(payloadJson);
        return {
          totalRequests: acc.totalRequests + 1,
          totalPipelineValueKc: acc.totalPipelineValueKc + (priceTotalKc || 0),
        };
      },
      { totalRequests: 0, totalPipelineValueKc: 0 }
    );
  }, [requests]);

  const latestAccessRequest = useMemo(() => {
    const requestsList = Array.isArray(userDetail && userDetail.access_requests) ? userDetail.access_requests : [];
    return requestsList[0] || null;
  }, [userDetail]);

  const onSaveRequest = useCallback(
    async (requestId) => {
      const draft = requestDrafts[requestId];
      if (!draft || !selectedAreaId) return;

      setRequestDrafts((prev) => ({
        ...prev,
        [requestId]: {
          ...prev[requestId],
          isSaving: true,
          error: "",
          successMessage: "",
        },
      }));

      try {
        const updated = await updateAccessRequest(requestId, {
          areaId: selectedAreaId,
          status: draft.status,
          notes: draft.notes,
        });
        const nextStatus =
          updated && typeof updated.status === "string" && updated.status
            ? updated.status
            : draft.status;
        const nextNotes =
          updated && Object.prototype.hasOwnProperty.call(updated, "notes")
            ? String(updated.notes || "")
            : draft.notes;

        setRequests((prev) =>
          prev.map((row) =>
            Number(row.id) === Number(requestId)
              ? { ...row, ...updated, status: nextStatus, notes: nextNotes }
              : row
          )
        );

        setRequestDrafts((prev) => ({
          ...prev,
          [requestId]: {
            ...(prev[requestId] || {}),
            status: nextStatus,
            notes: nextNotes,
            successMessage: "Saved",
            isSaving: false,
            error: "",
            copyMessage: prev[requestId] && prev[requestId].copyMessage ? prev[requestId].copyMessage : "",
          },
        }));
      } catch (error) {
        if (error && error.status === 401) {
          navigate("/app/login", { replace: true });
          return;
        }
        if (error && error.status === 403) {
          setAdminApiForbidden(true);
          if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
        }
        setRequestDrafts((prev) => ({
          ...prev,
          [requestId]: {
            ...prev[requestId],
            isSaving: false,
            error: error && error.status === 403 ? "Forbidden" : getErrorMessage(error),
            successMessage: "",
          },
        }));
      }
    },
    [auth, navigate, requestDrafts, selectedAreaId]
  );

  const onCopyPayload = useCallback(async (requestId, payloadJson) => {
    const payloadText = JSON.stringify(payloadJson, null, 2);

    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== "function") {
      setRequestDrafts((prev) => ({
        ...prev,
        [requestId]: {
          ...(prev[requestId] || {}),
          copyMessage: "Clipboard unavailable",
        },
      }));
      return;
    }

    try {
      await navigator.clipboard.writeText(payloadText);
      setRequestDrafts((prev) => ({
        ...prev,
        [requestId]: {
          ...(prev[requestId] || {}),
          copyMessage: "Payload copied",
        },
      }));
    } catch (_) {
      setRequestDrafts((prev) => ({
        ...prev,
        [requestId]: {
          ...(prev[requestId] || {}),
          copyMessage: "Copy failed",
        },
      }));
    }
  }, []);

  const onSendToPipedrive = useCallback(
    async (requestId) => {
      if (!selectedAreaId) return;

      setRequestDrafts((prev) => ({
        ...prev,
        [requestId]: {
          ...(prev[requestId] || {}),
          isPipedriveSending: true,
          pipedriveError: "",
          pipedriveMessage: "",
        },
      }));

      try {
        await pushRequestToPipedriveStub(requestId, selectedAreaId);
        setRequestDrafts((prev) => ({
          ...prev,
          [requestId]: {
            ...(prev[requestId] || {}),
            isPipedriveSending: false,
            pipedriveError: "",
            pipedriveMessage: "Pipedrive skeleton ready",
          },
        }));
      } catch (error) {
        if (error && error.status === 401) {
          navigate("/app/login", { replace: true });
          return;
        }
        if (error && error.status === 403) {
          setAdminApiForbidden(true);
          if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
        }
        setRequestDrafts((prev) => ({
          ...prev,
          [requestId]: {
            ...(prev[requestId] || {}),
            isPipedriveSending: false,
            pipedriveError: error && error.status === 403 ? "Forbidden" : getErrorMessage(error),
            pipedriveMessage: "",
          },
        }));
      }
    },
    [auth, navigate, selectedAreaId]
  );

  const onSubmitInvite = useCallback(
    async (event) => {
      event.preventDefault();
      if (!selectedAreaId) {
        setInviteError("Select area");
        return;
      }

      setInviteSubmitting(true);
      setInviteError("");
      setInviteResult(null);
      setCopyMessage("");

      try {
        const result = await createInvite({
          email: inviteEmail,
          areaId: selectedAreaId,
          role: inviteRole,
          enabledElections: enabledElectionIds,
          modules: sanitizeModulesMap(modulesMap),
          expiresInDays: Number(expiresInDays),
        });

        setInviteResult(result || null);
        setInviteEmail("");
      } catch (error) {
        if (error && error.status === 401) {
          navigate("/app/login", { replace: true });
          return;
        }
        if (error && error.status === 403) {
          setAdminApiForbidden(true);
          if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
          setInviteError("Forbidden");
          return;
        }
        setInviteError(getErrorMessage(error));
      } finally {
        setInviteSubmitting(false);
      }
    },
    [
      enabledElectionIds,
      expiresInDays,
      auth,
      inviteEmail,
      inviteRole,
      modulesMap,
      navigate,
      selectedAreaId,
    ]
  );

  const onUpdateMembershipDraft = useCallback((membershipId, updater) => {
    setMembershipDrafts((prev) => {
      const current = prev[membershipId] || buildMembershipDraft(null);
      const next = typeof updater === "function" ? updater(current) : { ...current, ...(updater || {}) };
      return {
        ...prev,
        [membershipId]: {
          ...next,
          error: "",
          successMessage: "",
        },
      };
    });
  }, []);

  const onSaveMembership = useCallback(
    async (membership) => {
      if (!membership || !userDetail || !userDetail.user) return;
      const draft = membershipDrafts[membership.id];
      if (!draft) return;

      setMembershipDrafts((prev) => ({
        ...prev,
        [membership.id]: {
          ...(prev[membership.id] || draft),
          isSaving: true,
          error: "",
          successMessage: "",
        },
      }));

      try {
        const updated = await updateAdminUserMembership(userDetail.user.id, membership.id, {
          enabled_elections: draft.enabledElectionIds,
          modules: sanitizeModulesMap(draft.modules),
          role: draft.role,
          is_active: draft.isActive,
        });

        setUserDetail((prev) => {
          if (!prev) return prev;
          const nextMemberships = (prev.memberships || []).map((item) =>
            Number(item.id) === Number(membership.id) ? { ...item, ...updated } : item
          );
          return {
            ...prev,
            memberships: nextMemberships,
          };
        });

        setMembershipDrafts((prev) => ({
          ...prev,
          [membership.id]: {
            ...buildMembershipDraft({ ...membership, ...updated }),
            successMessage: "Saved",
            isSaving: false,
          },
        }));
      } catch (error) {
        if (error && error.status === 401) {
          navigate("/app/login", { replace: true });
          return;
        }
        if (error && error.status === 403) {
          setAdminApiForbidden(true);
          if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
        }
        setMembershipDrafts((prev) => ({
          ...prev,
          [membership.id]: {
            ...(prev[membership.id] || draft),
            isSaving: false,
            error: error && error.status === 403 ? "Forbidden" : getErrorMessage(error),
            successMessage: "",
          },
        }));
      }
    },
    [auth, membershipDrafts, navigate, userDetail]
  );

  const onCreateMembership = useCallback(async () => {
    if (!userDetail || !userDetail.user || !newMembershipAreaId) return;

    setNewMembershipSubmitting(true);
    setNewMembershipError("");
    setNewMembershipMessage("");

    try {
      await createAdminUserMembership(userDetail.user.id, {
        area_id: newMembershipAreaId,
        role: newMembershipDraft.role,
        is_active: newMembershipDraft.isActive,
        enabled_elections: newMembershipDraft.enabledElectionIds,
        modules: sanitizeModulesMap(newMembershipDraft.modules),
      });
      await loadUserDetail(userDetail.user.id);
      setNewMembershipDraft(buildNewMembershipDraft());
      setNewMembershipMessage("Membership created");
    } catch (error) {
      if (error && error.status === 401) {
        navigate("/app/login", { replace: true });
        return;
      }
      if (error && error.status === 403) {
        setAdminApiForbidden(true);
        if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
      }
      setNewMembershipError(error && error.status === 403 ? "Forbidden" : getErrorMessage(error));
    } finally {
      setNewMembershipSubmitting(false);
    }
  }, [auth, loadUserDetail, navigate, newMembershipAreaId, newMembershipDraft, userDetail]);

  const onToggleUserActive = useCallback(async () => {
    if (!userDetail || !userDetail.user) return;

    const nextActive = !Boolean(userDetail.user.is_active);
    if (
      !nextActive &&
      typeof window !== "undefined" &&
      !window.confirm(`Deactivate user ${userDetail.user.email}?`)
    ) {
      return;
    }

    setUserActionPending("toggle-active");
    setUserActionError("");
    setUserActionMessage("");
    try {
      const updated = await updateAdminUser(userDetail.user.id, {
        is_active: nextActive,
      });
      setUserDetail((prev) => (prev ? { ...prev, user: updated } : prev));
      setUsers((prev) =>
        prev.map((user) => (Number(user.id) === Number(updated.id) ? { ...user, ...updated } : user))
      );
      setUserActionMessage(nextActive ? "User activated" : "User deactivated");
    } catch (error) {
      if (error && error.status === 401) {
        navigate("/app/login", { replace: true });
        return;
      }
      if (error && error.status === 403) {
        setAdminApiForbidden(true);
        if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
      }
      setUserActionError(error && error.status === 403 ? "Forbidden" : getErrorMessage(error));
    } finally {
      setUserActionPending("");
    }
  }, [auth, navigate, userDetail]);

  const onDeleteSelectedUser = useCallback(async () => {
    if (!userDetail || !userDetail.user) return;
    const email = userDetail.user.email;
    const confirmed =
      typeof window === "undefined"
        ? true
        : window.confirm(
            `Hard delete user ${email}? This removes the user row and cascades memberships, but keeps request history by email.`
          );
    if (!confirmed) return;

    setUserActionPending("delete-user");
    setUserActionError("");
    setUserActionMessage("");
    try {
      await deleteAdminUser(userDetail.user.id);
      setUsers((prev) => prev.filter((user) => Number(user.id) !== Number(userDetail.user.id)));
      setUserDetail(null);
      setSelectedUserId(null);
      setUserActionMessage(`Deleted ${email}`);
    } catch (error) {
      if (error && error.status === 401) {
        navigate("/app/login", { replace: true });
        return;
      }
      if (error && error.status === 403) {
        setAdminApiForbidden(true);
        if (typeof auth.markAdminForbidden === "function") auth.markAdminForbidden();
      }
      setUserActionError(error && error.status === 403 ? "Forbidden" : getErrorMessage(error));
    } finally {
      setUserActionPending("");
    }
  }, [auth, navigate, userDetail]);

  const onCopy = useCallback(async (value, label) => {
    try {
      await copyText(value);
      setCopyMessage(`${label} copied`);
    } catch (_) {
      setCopyMessage(`Copy failed for ${label.toLowerCase()}`);
    }
  }, []);

  const inviteActivateUrl = useMemo(() => {
    if (!inviteResult || !inviteResult.activate_url) return "";
    const raw = String(inviteResult.activate_url);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    if (typeof window === "undefined") return raw;
    return `${window.location.origin}${raw}`;
  }, [inviteResult]);

  const selectedUserSummary =
    filteredUsers.find((user) => Number(user.id) === Number(selectedUserId)) ||
    users.find((user) => Number(user.id) === Number(selectedUserId)) ||
    null;

  const allRequestedModuleKeys = useMemo(() => {
    const requestsList = Array.isArray(userDetail && userDetail.access_requests) ? userDetail.access_requests : [];
    return Array.from(
      new Set(
        requestsList
          .flatMap((request) => filterRealModuleKeys(normalizeStringList(request && request.requested_modules)))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "cs"));
  }, [userDetail]);

  const currentMembershipElectionIds = useMemo(() => {
    const memberships = Array.isArray(userDetail && userDetail.memberships) ? userDetail.memberships : [];
    return Array.from(
      new Set(memberships.flatMap((membership) => normalizeStringList(membership && membership.enabled_elections)))
    ).sort((a, b) => a.localeCompare(b, "cs"));
  }, [userDetail]);

  const currentMembershipModuleKeys = useMemo(() => {
    const memberships = Array.isArray(userDetail && userDetail.memberships) ? userDetail.memberships : [];
    return Array.from(
      new Set(memberships.flatMap((membership) => normalizeModuleKeysFromMap(membership && membership.modules)))
    ).sort((a, b) => a.localeCompare(b, "cs"));
  }, [userDetail]);

  const newMembershipModuleKeys = useMemo(
    () => buildModuleKeys(REAL_MODULE_KEYS, allRequestedModuleKeys, newMembershipDraft.modules),
    [allRequestedModuleKeys, newMembershipDraft.modules]
  );

  return (
    <div style={adminStyles.container}>
      <section style={adminStyles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0 }}>Admin</h2>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            {isSuperadmin ? "Superadmin" : `Role: ${selectedMembership ? selectedMembership.role : "N/A"}`}
          </div>
        </div>

        <div style={adminStyles.controlsRow}>
          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 13 }}>Area</span>
            <select
              style={adminStyles.select}
              value={selectedAreaId || ""}
              onChange={(event) => {
                const nextAreaId = toPositiveIntOrNull(event.target.value);
                setSelectedAreaId(nextAreaId);
              }}
              disabled={areaOptions.length <= 1}
            >
              {areaOptions.length === 0 ? (
                <option value="">No areas</option>
              ) : null}
              {areaOptions.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name} ({area.id})
                </option>
              ))}
            </select>
          </label>
          {activeTab === "users" ? (
            <div style={{ fontSize: 12, opacity: 0.8, alignSelf: "flex-end" }}>
              Users tab is global; area selector is only used for requests and invites.
            </div>
          ) : areaOptions.length > 1 ? (
            <div style={{ fontSize: 12, opacity: 0.8, alignSelf: "flex-end" }}>
              Use the dropdown to switch `area_id`.
            </div>
          ) : null}
        </div>

        <div style={adminStyles.controlsRow}>
          <button
            type="button"
            style={{
              ...adminStyles.tabBtn,
              ...(activeTab === "requests" ? adminStyles.tabBtnActive : null),
            }}
            onClick={() => navigate("/app/admin/requests")}
          >
            Access Requests
          </button>
          <button
            type="button"
            style={{
              ...adminStyles.tabBtn,
              ...(activeTab === "invites" ? adminStyles.tabBtnActive : null),
            }}
            onClick={() => navigate("/app/admin/invites")}
          >
            Create Invite
          </button>
          {isSuperadmin ? (
            <button
              type="button"
              style={{
                ...adminStyles.tabBtn,
                ...(activeTab === "users" ? adminStyles.tabBtnActive : null),
              }}
              onClick={() => navigate("/app/admin/users")}
            >
              Users
            </button>
          ) : null}
        </div>

        {areaOptions.length === 0 && activeTab !== "users" ? (
          <div style={adminStyles.error}>No accessible areas found.</div>
        ) : null}
        {shouldShowForbidden ? (
          <div style={adminStyles.error}>Forbidden</div>
        ) : null}
      </section>

      {activeTab === "users" ? (
        <section style={adminStyles.card}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0 }}>User Management</h3>
            <div style={adminStyles.controlsRow}>
              <input
                type="search"
                placeholder="Search name or email"
                style={{ ...adminStyles.input, width: 260 }}
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                disabled={!isSuperadmin}
              />
              <button type="button" style={adminStyles.button} onClick={loadUsers} disabled={usersLoading || !isSuperadmin}>
                {usersLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          {!isSuperadmin ? <div style={adminStyles.error}>Superadmin only.</div> : null}
          {usersError ? <div style={adminStyles.error}>{usersError}</div> : null}

          <div style={adminStyles.summaryGrid}>
            <div style={adminStyles.summaryStat}>
              <div style={adminStyles.summaryLabel}>Users</div>
              <div style={adminStyles.summaryValue}>{users.length}</div>
            </div>
            <div style={adminStyles.summaryStat}>
              <div style={adminStyles.summaryLabel}>Filtered</div>
              <div style={adminStyles.summaryValue}>{filteredUsers.length}</div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              alignItems: "start",
            }}
          >
            <div style={adminStyles.sidePanel}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Users</div>
              <div style={adminStyles.sideList}>
                {!usersLoading && filteredUsers.length === 0 ? (
                  <div style={adminStyles.message}>No users found.</div>
                ) : null}
                {filteredUsers.map((user) => {
                  const isSelected = Number(selectedUserId) === Number(user.id);
                  return (
                    <button
                      key={user.id}
                      type="button"
                      style={{
                        ...adminStyles.listItemButton,
                        ...(isSelected ? adminStyles.listItemButtonActive : null),
                      }}
                      onClick={() => setSelectedUserId(user.id)}
                    >
                      <div style={adminStyles.listItemTitle}>{user.name || "Unnamed user"}</div>
                      <div style={adminStyles.listItemMeta}>{user.email}</div>
                      <div style={adminStyles.badgeRow}>
                        <span style={adminStyles.badge}>{user.is_active ? "active" : "inactive"}</span>
                        <span style={adminStyles.badge}>{user.is_superadmin ? "superadmin" : "user"}</span>
                        {user.default_area_id ? (
                          <span style={adminStyles.badge}>
                            default_area_id {user.default_area_id}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              {userDetailError ? <div style={adminStyles.error}>{userDetailError}</div> : null}
              {userDetailLoading ? <div style={adminStyles.message}>Loading user detail...</div> : null}
              {!selectedUserSummary && !userDetailLoading ? (
                <div style={adminStyles.message}>Select a user to inspect memberships and request history.</div>
              ) : null}

              {selectedUserSummary &&
              userDetail &&
              userDetail.user &&
              Number(userDetail.user.id) === Number(selectedUserSummary.id) ? (
                <>
                  <div style={adminStyles.detailsPanel}>
                    <div style={adminStyles.detailsGrid}>
                      <div style={adminStyles.detailsSection}>
                        <div style={adminStyles.detailsLabel}>User</div>
                        <div style={adminStyles.detailsText}>{userDetail.user.name || "—"}</div>
                      </div>
                      <div style={adminStyles.detailsSection}>
                        <div style={adminStyles.detailsLabel}>Email</div>
                        <div style={adminStyles.detailsText}>{userDetail.user.email}</div>
                      </div>
                      <div style={adminStyles.detailsSection}>
                        <div style={adminStyles.detailsLabel}>Flags</div>
                        <div style={adminStyles.wrappedList}>
                          <span style={adminStyles.wrappedListItem}>
                            {userDetail.user.is_active ? "active" : "inactive"}
                          </span>
                          <span style={adminStyles.wrappedListItem}>
                            {userDetail.user.is_superadmin ? "superadmin" : "standard user"}
                          </span>
                        </div>
                      </div>
                      <div style={adminStyles.detailsSection}>
                        <div style={adminStyles.detailsLabel}>Default area</div>
                        <div style={adminStyles.detailsText}>
                          {userDetail.user.default_area_id
                            ? `${userDetail.user.default_area_name || "Area"} (${userDetail.user.default_area_id})`
                            : "—"}
                        </div>
                      </div>
                      <div style={adminStyles.detailsSection}>
                        <div style={adminStyles.detailsLabel}>Created</div>
                        <div style={adminStyles.detailsText}>
                          {formatDateTimeParts(userDetail.user.created_at).dateLabel || "—"}{" "}
                          {formatDateTimeParts(userDetail.user.created_at).timeLabel || ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        type="button"
                        style={{
                          ...adminStyles.button,
                          ...(userActionPending === "toggle-active" ? adminStyles.saveButtonDisabled : null),
                        }}
                        onClick={onToggleUserActive}
                        disabled={userActionPending === "toggle-active" || userActionPending === "delete-user"}
                      >
                        {userActionPending === "toggle-active"
                          ? "Saving..."
                          : userDetail.user.is_active
                            ? "Deactivate user"
                            : "Activate user"}
                      </button>
                      <button
                        type="button"
                        style={{
                          ...adminStyles.dangerBtn,
                          ...(userActionPending === "delete-user" ? adminStyles.saveButtonDisabled : null),
                        }}
                        onClick={onDeleteSelectedUser}
                        disabled={userActionPending === "toggle-active" || userActionPending === "delete-user"}
                      >
                        {userActionPending === "delete-user" ? "Deleting..." : "Delete user"}
                      </button>
                      {userActionMessage ? (
                        <span style={{ fontSize: 12, color: "#166534" }}>{userActionMessage}</span>
                      ) : null}
                      {userActionError ? (
                        <span style={{ fontSize: 12, color: "#991b1b" }}>{userActionError}</span>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: 12 }}>
                    <h4 style={{ margin: 0 }}>Add Membership</h4>
                    {availableAreasForNewMembership.length === 0 ? (
                      <div style={adminStyles.message}>All runtime areas are already assigned to this user.</div>
                    ) : (
                      <div style={adminStyles.membershipCard}>
                        <div style={adminStyles.fieldGrid}>
                          <label style={{ display: "grid", gap: 6 }}>
                            <span style={{ fontSize: 13 }}>Area</span>
                            <select
                              style={adminStyles.select}
                              value={newMembershipAreaId || ""}
                              onChange={(event) => setNewMembershipAreaId(toPositiveIntOrNull(event.target.value))}
                              disabled={newMembershipSubmitting}
                            >
                              {availableAreasForNewMembership.map((area) => (
                                <option key={area.id} value={area.id}>
                                  {area.name || `Area ${area.id}`} ({area.id})
                                </option>
                              ))}
                            </select>
                          </label>
                          <label style={{ display: "grid", gap: 6 }}>
                            <span style={{ fontSize: 13 }}>Role</span>
                            <select
                              style={adminStyles.select}
                              value={newMembershipDraft.role}
                              onChange={(event) =>
                                setNewMembershipDraft((prev) => ({
                                  ...prev,
                                  role: event.target.value,
                                }))
                              }
                              disabled={newMembershipSubmitting}
                            >
                              {INVITE_ROLES.map((role) => (
                                <option key={role} value={role}>
                                  {role}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label style={{ display: "flex", gap: 8, alignItems: "center", alignSelf: "end" }}>
                            <input
                              type="checkbox"
                              checked={newMembershipDraft.isActive}
                              onChange={(event) =>
                                setNewMembershipDraft((prev) => ({
                                  ...prev,
                                  isActive: event.target.checked,
                                }))
                              }
                              disabled={newMembershipSubmitting}
                            />
                            <span style={{ fontSize: 13 }}>Membership active</span>
                          </label>
                        </div>

                        <label style={{ display: "grid", gap: 6 }}>
                          <span style={{ fontSize: 13 }}>enabled_elections</span>
                          <select
                            multiple
                            size={Math.min(8, Math.max(4, newMembershipElectionsCatalog.length || 4))}
                            style={{ ...adminStyles.select, minHeight: 110 }}
                            value={newMembershipDraft.enabledElectionIds}
                            onChange={(event) =>
                              setNewMembershipDraft((prev) => ({
                                ...prev,
                                enabledElectionIds: Array.from(event.target.selectedOptions).map((opt) => opt.value),
                              }))
                            }
                            disabled={newMembershipSubmitting || newMembershipElectionsLoading}
                          >
                            {newMembershipElectionsCatalog.map((item) => (
                              <option key={String(item.id)} value={String(item.id)}>
                                {String(item.display_name || item.name || item.id)} ({String(item.id)})
                              </option>
                            ))}
                          </select>
                          {newMembershipElectionsLoading ? (
                            <span style={{ fontSize: 12, opacity: 0.8 }}>Loading elections...</span>
                          ) : null}
                          {newMembershipElectionsError ? (
                            <span style={{ fontSize: 12, color: "#991b1b" }}>{newMembershipElectionsError}</span>
                          ) : null}
                        </label>

                        <div style={{ display: "grid", gap: 8 }}>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>modules</div>
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            {newMembershipModuleKeys.map((moduleKey) => (
                              <label key={moduleKey} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(newMembershipDraft.modules && newMembershipDraft.modules[moduleKey])}
                                  onChange={(event) =>
                                    setNewMembershipDraft((prev) => ({
                                      ...prev,
                                      modules: {
                                        ...(prev.modules || {}),
                                        [moduleKey]: event.target.checked,
                                      },
                                    }))
                                  }
                                  disabled={newMembershipSubmitting}
                                />
                                <span>{getRequestModuleLabel(moduleKey)}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            style={{
                              ...adminStyles.primaryBtn,
                              ...(newMembershipSubmitting ? adminStyles.saveButtonDisabled : null),
                            }}
                            onClick={onCreateMembership}
                            disabled={newMembershipSubmitting || !newMembershipAreaId}
                          >
                            {newMembershipSubmitting ? "Creating..." : "Create membership"}
                          </button>
                          {newMembershipMessage ? (
                            <span style={{ fontSize: 12, color: "#166534" }}>{newMembershipMessage}</span>
                          ) : null}
                          {newMembershipError ? (
                            <span style={{ fontSize: 12, color: "#991b1b" }}>{newMembershipError}</span>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ display: "grid", gap: 12 }}>
                    <h4 style={{ margin: 0 }}>Memberships</h4>
                    {(userDetail.memberships || []).length === 0 ? (
                      <div style={adminStyles.message}>This user has no memberships yet.</div>
                    ) : (
                      (userDetail.memberships || []).map((membership) => {
                        const draft = membershipDrafts[membership.id] || buildMembershipDraft(membership);
                        const availableElections = sortElectionsNewestFirst(
                          Array.isArray(membership.available_elections) ? membership.available_elections : []
                        );
                        const moduleKeys = buildModuleKeys(
                          REAL_MODULE_KEYS,
                          draft.modules,
                          allRequestedModuleKeys
                        );
                        const electionDiff = diffStringLists(
                          latestAccessRequest && latestAccessRequest.requested_elections,
                          draft.enabledElectionIds
                        );
                        const moduleDiff = diffStringLists(
                          filterRealModuleKeys(normalizeStringList(latestAccessRequest && latestAccessRequest.requested_modules)),
                          normalizeModuleKeysFromMap(draft.modules)
                        );

                        return (
                          <div key={membership.id} style={adminStyles.membershipCard}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                              <div>
                                <div style={{ fontSize: 16, fontWeight: 700 }}>
                                  {membership.area_name || `Area ${membership.area_id}`}
                                </div>
                                <div style={{ fontSize: 12, color: "#475569" }}>
                                  membership #{membership.id} • area_id {membership.area_id}
                                </div>
                              </div>
                              <div style={adminStyles.badgeRow}>
                                <span style={adminStyles.badge}>{draft.isActive ? "active" : "inactive"}</span>
                                <span style={adminStyles.badge}>role {draft.role}</span>
                              </div>
                            </div>

                            <div style={adminStyles.fieldGrid}>
                              <label style={{ display: "grid", gap: 6 }}>
                                <span style={{ fontSize: 13 }}>Role</span>
                                <select
                                  style={adminStyles.select}
                                  value={draft.role}
                                  onChange={(event) =>
                                    onUpdateMembershipDraft(membership.id, { role: event.target.value })
                                  }
                                  disabled={draft.isSaving}
                                >
                                  {INVITE_ROLES.map((role) => (
                                    <option key={role} value={role}>
                                      {role}
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label style={{ display: "flex", gap: 8, alignItems: "center", alignSelf: "end" }}>
                                <input
                                  type="checkbox"
                                  checked={draft.isActive}
                                  onChange={(event) =>
                                    onUpdateMembershipDraft(membership.id, { isActive: event.target.checked })
                                  }
                                  disabled={draft.isSaving}
                                />
                                <span style={{ fontSize: 13 }}>Membership active</span>
                              </label>
                            </div>

                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 13 }}>enabled_elections</span>
                              <select
                                multiple
                                size={Math.min(8, Math.max(4, availableElections.length || 4))}
                                style={{ ...adminStyles.select, minHeight: 110 }}
                                value={draft.enabledElectionIds}
                                onChange={(event) =>
                                  onUpdateMembershipDraft(membership.id, {
                                    enabledElectionIds: Array.from(event.target.selectedOptions).map((opt) => opt.value),
                                  })
                                }
                                disabled={draft.isSaving}
                              >
                                {availableElections.map((item) => (
                                  <option key={String(item.id)} value={String(item.id)}>
                                    {String(item.display_name || item.name || item.id)} ({String(item.id)})
                                  </option>
                                ))}
                              </select>
                            </label>

                            <div style={{ display: "grid", gap: 8 }}>
                              <div style={{ fontSize: 13, fontWeight: 700 }}>modules</div>
                              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                                {moduleKeys.map((moduleKey) => (
                                  <label key={moduleKey} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(draft.modules && draft.modules[moduleKey])}
                                      onChange={(event) =>
                                        onUpdateMembershipDraft(membership.id, (current) => ({
                                          ...current,
                                          modules: {
                                            ...(current.modules || {}),
                                            [moduleKey]: event.target.checked,
                                          },
                                        }))
                                      }
                                      disabled={draft.isSaving}
                                    />
                                    <span>{getRequestModuleLabel(moduleKey)}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            <div style={{ display: "grid", gap: 10 }}>
                              <div style={{ fontSize: 13, fontWeight: 700 }}>
                                Requested vs enabled
                                {latestAccessRequest ? ` (request #${latestAccessRequest.id})` : ""}
                              </div>
                              {latestAccessRequest ? (
                                <div style={adminStyles.compareGrid}>
                                  <div style={adminStyles.compareCard}>
                                    <div style={adminStyles.detailsLabel}>Requested and enabled elections</div>
                                    <div style={adminStyles.wrappedList}>
                                      {electionDiff.shared.length > 0 ? (
                                        electionDiff.shared.map((item) => (
                                          <span key={item} style={adminStyles.wrappedListItem}>
                                            {item}
                                          </span>
                                        ))
                                      ) : (
                                        <span style={adminStyles.detailsText}>—</span>
                                      )}
                                    </div>
                                  </div>
                                  <div style={adminStyles.compareCard}>
                                    <div style={adminStyles.detailsLabel}>Requested but not enabled</div>
                                    <div style={adminStyles.wrappedList}>
                                      {electionDiff.requestedOnly.length > 0 ? (
                                        electionDiff.requestedOnly.map((item) => (
                                          <span key={item} style={adminStyles.wrappedListItem}>
                                            {item}
                                          </span>
                                        ))
                                      ) : (
                                        <span style={adminStyles.detailsText}>—</span>
                                      )}
                                    </div>
                                  </div>
                                  <div style={adminStyles.compareCard}>
                                    <div style={adminStyles.detailsLabel}>Enabled but not requested</div>
                                    <div style={adminStyles.wrappedList}>
                                      {electionDiff.enabledOnly.length > 0 ? (
                                        electionDiff.enabledOnly.map((item) => (
                                          <span key={item} style={adminStyles.wrappedListItem}>
                                            {item}
                                          </span>
                                        ))
                                      ) : (
                                        <span style={adminStyles.detailsText}>—</span>
                                      )}
                                    </div>
                                  </div>
                                  <div style={adminStyles.compareCard}>
                                    <div style={adminStyles.detailsLabel}>Requested and enabled modules</div>
                                    <div style={adminStyles.wrappedList}>
                                      {moduleDiff.shared.length > 0 ? (
                                        moduleDiff.shared.map((item) => (
                                          <span key={item} style={adminStyles.wrappedListItem}>
                                            {getRequestModuleLabel(item)}
                                          </span>
                                        ))
                                      ) : (
                                        <span style={adminStyles.detailsText}>—</span>
                                      )}
                                    </div>
                                  </div>
                                  <div style={adminStyles.compareCard}>
                                    <div style={adminStyles.detailsLabel}>Requested but not enabled modules</div>
                                    <div style={adminStyles.wrappedList}>
                                      {moduleDiff.requestedOnly.length > 0 ? (
                                        moduleDiff.requestedOnly.map((item) => (
                                          <span key={item} style={adminStyles.wrappedListItem}>
                                            {getRequestModuleLabel(item)}
                                          </span>
                                        ))
                                      ) : (
                                        <span style={adminStyles.detailsText}>—</span>
                                      )}
                                    </div>
                                  </div>
                                  <div style={adminStyles.compareCard}>
                                    <div style={adminStyles.detailsLabel}>Enabled but not requested modules</div>
                                    <div style={adminStyles.wrappedList}>
                                      {moduleDiff.enabledOnly.length > 0 ? (
                                        moduleDiff.enabledOnly.map((item) => (
                                          <span key={item} style={adminStyles.wrappedListItem}>
                                            {getRequestModuleLabel(item)}
                                          </span>
                                        ))
                                      ) : (
                                        <span style={adminStyles.detailsText}>—</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div style={adminStyles.message}>No access request found for this email yet.</div>
                              )}
                            </div>

                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                              <button
                                type="button"
                                style={{
                                  ...adminStyles.primaryBtn,
                                  ...(draft.isSaving ? adminStyles.saveButtonDisabled : null),
                                }}
                                onClick={() => onSaveMembership(membership)}
                                disabled={draft.isSaving}
                              >
                                {draft.isSaving ? "Saving..." : "Save membership"}
                              </button>
                              {draft.successMessage ? (
                                <span style={{ fontSize: 12, color: "#166534" }}>{draft.successMessage}</span>
                              ) : null}
                              {draft.error ? (
                                <span style={{ fontSize: 12, color: "#991b1b" }}>{draft.error}</span>
                              ) : null}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div style={{ display: "grid", gap: 12 }}>
                    <h4 style={{ margin: 0 }}>Original Access Requests</h4>
                    {(userDetail.access_requests || []).length === 0 ? (
                      <div style={adminStyles.message}>No access request matched this email.</div>
                    ) : (
                      (userDetail.access_requests || []).map((request) => {
                        const createdAt = formatDateTimeParts(request.created_at);
                        const electionDiff = diffStringLists(request.requested_elections, currentMembershipElectionIds);
                        const requestedModuleKeys = filterRealModuleKeys(normalizeStringList(request.requested_modules));
                        const moduleDiff = diffStringLists(requestedModuleKeys, currentMembershipModuleKeys);
                        const breakdownEntries = getBreakdownEntries(request.price_breakdown);
                        return (
                          <div key={request.id} style={adminStyles.requestCard}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                              <div>
                                <div style={{ fontSize: 16, fontWeight: 700 }}>Request #{request.id}</div>
                                <div style={{ fontSize: 12, color: "#475569" }}>
                                  {createdAt.dateLabel || "—"} {createdAt.timeLabel || ""}
                                </div>
                              </div>
                              <div style={adminStyles.badgeRow}>
                                {request.status ? <span style={adminStyles.badge}>{request.status}</span> : null}
                                {request.pricing_version ? (
                                  <span style={adminStyles.badge}>{request.pricing_version}</span>
                                ) : null}
                              </div>
                            </div>

                            <div style={adminStyles.detailsGrid}>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Name</div>
                                <div style={adminStyles.detailsText}>{request.name || "—"}</div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Email</div>
                                <div style={adminStyles.detailsText}>{request.email || "—"}</div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Phone</div>
                                <div style={adminStyles.detailsText}>{request.phone || "—"}</div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Requested area</div>
                                <div style={adminStyles.detailsText}>{request.requested_area_text || "—"}</div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Requested elections</div>
                                <div style={adminStyles.wrappedList}>
                                  {request.requested_elections && request.requested_elections.length > 0 ? (
                                    sortElectionsNewestFirst(request.requested_elections).map((item) => (
                                      <span key={item} style={adminStyles.wrappedListItem}>
                                        {item}
                                      </span>
                                    ))
                                  ) : (
                                    <span style={adminStyles.detailsText}>—</span>
                                  )}
                                </div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Requested modules</div>
                                <div style={adminStyles.wrappedList}>
                                  {requestedModuleKeys.length > 0 ? (
                                    requestedModuleKeys.map((item) => (
                                      <span key={item} style={adminStyles.wrappedListItem}>
                                        {getRequestModuleLabel(item)}
                                      </span>
                                    ))
                                  ) : (
                                    <span style={adminStyles.detailsText}>—</span>
                                  )}
                                </div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Client note</div>
                                <div style={adminStyles.detailsText}>{request.note || "—"}</div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Pricing version</div>
                                <div style={adminStyles.detailsText}>{request.pricing_version || "—"}</div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Price total</div>
                                <div style={adminStyles.detailsText}>{formatPriceKc(request.price_total_kc)}</div>
                              </div>
                              <div style={adminStyles.detailsSection}>
                                <div style={adminStyles.detailsLabel}>Price breakdown</div>
                                {breakdownEntries.length > 0 ? (
                                  <div style={{ display: "grid", gap: 6 }}>
                                    {breakdownEntries.map(([groupKey, groupValue]) => (
                                      <div key={groupKey} style={adminStyles.detailsText}>
                                        {isPlainObject(groupValue)
                                          ? Object.entries(groupValue)
                                              .map(([itemKey, amount]) =>
                                                formatBreakdownValue(groupKey, itemKey, amount)
                                              )
                                              .join(", ")
                                          : `${groupKey}: ${JSON.stringify(groupValue)}`}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div style={adminStyles.detailsText}>—</div>
                                )}
                              </div>
                            </div>

                            <details>
                              <summary>Raw access_request payload</summary>
                              <pre style={adminStyles.pre}>{JSON.stringify(request.payload_json, null, 2)}</pre>
                            </details>

                            <div style={adminStyles.compareGrid}>
                              <div style={adminStyles.compareCard}>
                                <div style={adminStyles.detailsLabel}>Requested but not enabled elections</div>
                                <div style={adminStyles.wrappedList}>
                                  {electionDiff.requestedOnly.length > 0 ? (
                                    electionDiff.requestedOnly.map((item) => (
                                      <span key={item} style={adminStyles.wrappedListItem}>
                                        {item}
                                      </span>
                                    ))
                                  ) : (
                                    <span style={adminStyles.detailsText}>—</span>
                                  )}
                                </div>
                              </div>
                              <div style={adminStyles.compareCard}>
                                <div style={adminStyles.detailsLabel}>Enabled but not requested elections</div>
                                <div style={adminStyles.wrappedList}>
                                  {electionDiff.enabledOnly.length > 0 ? (
                                    electionDiff.enabledOnly.map((item) => (
                                      <span key={item} style={adminStyles.wrappedListItem}>
                                        {item}
                                      </span>
                                    ))
                                  ) : (
                                    <span style={adminStyles.detailsText}>—</span>
                                  )}
                                </div>
                              </div>
                              <div style={adminStyles.compareCard}>
                                <div style={adminStyles.detailsLabel}>Requested but not enabled modules</div>
                                <div style={adminStyles.wrappedList}>
                                  {moduleDiff.requestedOnly.length > 0 ? (
                                    moduleDiff.requestedOnly.map((item) => (
                                      <span key={item} style={adminStyles.wrappedListItem}>
                                        {getRequestModuleLabel(item)}
                                      </span>
                                    ))
                                  ) : (
                                    <span style={adminStyles.detailsText}>—</span>
                                  )}
                                </div>
                              </div>
                              <div style={adminStyles.compareCard}>
                                <div style={adminStyles.detailsLabel}>Enabled but not requested modules</div>
                                <div style={adminStyles.wrappedList}>
                                  {moduleDiff.enabledOnly.length > 0 ? (
                                    moduleDiff.enabledOnly.map((item) => (
                                      <span key={item} style={adminStyles.wrappedListItem}>
                                        {getRequestModuleLabel(item)}
                                      </span>
                                    ))
                                  ) : (
                                    <span style={adminStyles.detailsText}>—</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {activeTab === "requests" ? (
        <section style={adminStyles.card}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0 }}>Access Requests</h3>
            <div style={adminStyles.controlsRow}>
              <select
                style={adminStyles.select}
                value={requestStatusFilter}
                onChange={(event) => setRequestStatusFilter(event.target.value)}
                disabled={shouldShowForbidden || !selectedAreaId}
              >
                <option value="">All statuses</option>
                {REQUEST_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <button
                type="button"
                style={adminStyles.button}
                onClick={loadRequests}
                disabled={requestsLoading || shouldShowForbidden || !selectedAreaId}
              >
                {requestsLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          {requestsError ? <div style={adminStyles.error}>{requestsError}</div> : null}
          <div style={adminStyles.summaryGrid}>
            <div style={adminStyles.summaryStat}>
              <div style={adminStyles.summaryLabel}>Total Requests</div>
              <div style={adminStyles.summaryValue}>{requestsSummary.totalRequests}</div>
            </div>
            <div style={adminStyles.summaryStat}>
              <div style={adminStyles.summaryLabel}>Total Pipeline Value (Kč)</div>
              <div style={adminStyles.summaryValue}>{formatPriceKc(requestsSummary.totalPipelineValueKc)}</div>
            </div>
          </div>
          {!requestsError && requests.length === 0 && !requestsLoading ? (
            <div style={adminStyles.message}>No access requests found for current filter.</div>
          ) : null}

          <div style={adminStyles.tableWrapper}>
            <table style={adminStyles.table}>
              <colgroup>
                {REQUEST_TABLE_COLUMNS.map((column, index) => (
                  <col key={index} style={column} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th style={adminStyles.tableHeaderCell}>created_at</th>
                  <th style={adminStyles.tableHeaderCell}>email</th>
                  <th style={adminStyles.tableHeaderCell}>name</th>
                  <th style={adminStyles.tableHeaderCell}>phone</th>
                  <th style={adminStyles.tableHeaderCell}>requested_area_text</th>
                  <th style={adminStyles.tableHeaderCell}>Modules</th>
                  <th style={adminStyles.tableHeaderCell}>Estimated Value</th>
                  <th style={adminStyles.tableHeaderCell}>status</th>
                  <th style={adminStyles.tableHeaderCell}>Save</th>
                  <th style={adminStyles.tableHeaderCell}>CRM</th>
                  <th style={adminStyles.tableHeaderCell}>Details</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((row) => {
                  const payloadJson = getRequestPayloadJson(row);
                  const elections = sortElectionsNewestFirst(normalizeStringList(payloadJson && payloadJson.elections));
                  const modules = filterRealModuleKeys(normalizeStringList(payloadJson && payloadJson.modules));
                  const note =
                    payloadJson && typeof payloadJson.note === "string" ? payloadJson.note : "";
                  const priceTotalKc = getRequestPriceTotalKc(payloadJson);
                  const createdAt = formatDateTimeParts(row.created_at);
                  const priceBreakdown =
                    payloadJson && isPlainObject(payloadJson.price_breakdown)
                      ? payloadJson.price_breakdown
                      : null;
                  const breakdownEntries = getBreakdownEntries(priceBreakdown);
                  const isExpanded = Number(expandedRequestId) === Number(row.id);
                  const draft = requestDrafts[row.id] || {
                    status: row.status || "new",
                    notes: row.notes || "",
                    isSaving: false,
                    isPipedriveSending: false,
                    error: "",
                    successMessage: "",
                    pipedriveError: "",
                    pipedriveMessage: "",
                    copyMessage: "",
                  };
                  return (
                    <React.Fragment key={row.id}>
                      <tr style={{ borderTop: "1px solid #e2e8f0" }}>
                        <td style={adminStyles.tableCell}>
                          <div style={adminStyles.stackedDateTime}>
                            <span style={adminStyles.stackedDatePrimary}>{createdAt.dateLabel || "—"}</span>
                            {createdAt.timeLabel ? (
                              <span style={adminStyles.stackedDateSecondary}>{createdAt.timeLabel}</span>
                            ) : null}
                          </div>
                        </td>
                        <td style={adminStyles.tableCell}>{row.email || "—"}</td>
                        <td style={adminStyles.tableCell}>{row.name || "—"}</td>
                        <td style={adminStyles.tableCell}>{row.phone || "—"}</td>
                        <td style={adminStyles.tableCell}>{row.requested_area_text || "—"}</td>
                        <td
                          style={adminStyles.tableCell}
                          title={modules.length > 0 ? modules.map(getRequestModuleLabel).join(", ") : undefined}
                        >
                          {getCompactModulesLabel(modules)}
                        </td>
                        <td style={{ ...adminStyles.tableCell, whiteSpace: "nowrap" }}>
                          {priceTotalKc === null ? "—" : formatPriceKc(priceTotalKc)}
                        </td>
                        <td style={adminStyles.tableCell}>
                          <select
                            style={{ ...adminStyles.select, ...adminStyles.rowActionSelect }}
                            value={draft.status}
                            onChange={(event) => {
                              const nextStatus = event.target.value;
                              setRequestDrafts((prev) => ({
                                ...prev,
                                [row.id]: {
                                  ...(prev[row.id] || draft),
                                  successMessage: "",
                                  error: "",
                                  status: nextStatus,
                                },
                              }));
                            }}
                            disabled={draft.isSaving || shouldShowForbidden}
                          >
                            {REQUEST_STATUSES.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={adminStyles.tableCell}>
                          <button
                            type="button"
                            style={{
                              ...adminStyles.saveButton,
                              ...adminStyles.rowButton,
                              ...(draft.isSaving || shouldShowForbidden ? adminStyles.saveButtonDisabled : null),
                            }}
                            onClick={() => onSaveRequest(row.id)}
                            disabled={draft.isSaving || shouldShowForbidden}
                          >
                            {draft.isSaving ? "Saving..." : "Save"}
                          </button>
                          {draft.successMessage ? (
                            <div style={{ ...adminStyles.inlineFeedback, color: "#166534" }}>
                              {draft.successMessage}
                            </div>
                          ) : null}
                          {draft.error ? (
                            <div style={{ ...adminStyles.inlineFeedback, color: "#991b1b" }}>
                              {draft.error}
                            </div>
                          ) : null}
                        </td>
                        <td style={adminStyles.tableCell}>
                          <button
                            type="button"
                            style={{
                              ...adminStyles.button,
                              ...adminStyles.rowButton,
                              ...(draft.isPipedriveSending || shouldShowForbidden
                                ? adminStyles.saveButtonDisabled
                                : null),
                            }}
                            onClick={() => onSendToPipedrive(row.id)}
                            disabled={draft.isPipedriveSending || shouldShowForbidden}
                          >
                            {draft.isPipedriveSending ? "Sending..." : "Send to Pipedrive"}
                          </button>
                          {draft.pipedriveMessage ? (
                            <div style={{ ...adminStyles.inlineFeedback, color: "#166534" }}>
                              {draft.pipedriveMessage}
                            </div>
                          ) : null}
                          {draft.pipedriveError ? (
                            <div style={{ ...adminStyles.inlineFeedback, color: "#991b1b" }}>
                              {draft.pipedriveError}
                            </div>
                          ) : null}
                        </td>
                        <td style={adminStyles.tableCell}>
                          <button
                            type="button"
                            style={{ ...adminStyles.button, ...adminStyles.rowButton }}
                            onClick={() =>
                              setExpandedRequestId((prev) =>
                                Number(prev) === Number(row.id) ? null : row.id
                              )
                            }
                            aria-expanded={isExpanded}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr style={{ borderTop: "1px solid #e2e8f0" }}>
                          <td colSpan={11} style={{ padding: "0 6px 10px" }}>
                            <div style={adminStyles.detailsPanel}>
                              <div style={adminStyles.controlsRow}>
                                <button
                                  type="button"
                                  style={adminStyles.button}
                                  onClick={() => onCopyPayload(row.id, payloadJson)}
                                >
                                  Copy payload
                                </button>
                                {draft.copyMessage ? (
                                  <div style={{ fontSize: 12, color: "#0f172a" }}>{draft.copyMessage}</div>
                                ) : null}
                              </div>

                              <div style={adminStyles.detailsGrid}>
                                <div style={adminStyles.detailsSection}>
                                  <div style={adminStyles.detailsLabel}>Elections list</div>
                                  <div style={adminStyles.wrappedList}>
                                    {elections.length > 0 ? (
                                      elections.map((electionId) => (
                                        <span key={electionId} style={adminStyles.wrappedListItem}>
                                          {electionId}
                                        </span>
                                      ))
                                    ) : (
                                      <span style={adminStyles.detailsText}>—</span>
                                    )}
                                  </div>
                                </div>

                                <div style={adminStyles.detailsSection}>
                                  <div style={adminStyles.detailsLabel}>Modules list</div>
                                  <div style={adminStyles.wrappedList}>
                                    {modules.length > 0 ? (
                                      modules.map((moduleKey) => (
                                        <span key={moduleKey} style={adminStyles.wrappedListItem}>
                                          {getRequestModuleLabel(moduleKey)}
                                        </span>
                                      ))
                                    ) : (
                                      <span style={adminStyles.detailsText}>—</span>
                                    )}
                                  </div>
                                </div>

                                <div style={adminStyles.detailsSection}>
                                  <div style={adminStyles.detailsLabel}>Client note</div>
                                  <div style={adminStyles.detailsText}>{note || "—"}</div>
                                </div>

                                <div style={adminStyles.detailsSection}>
                                  <div style={adminStyles.detailsLabel}>Price breakdown</div>
                                  {breakdownEntries.length > 0 ? (
                                    <div style={{ display: "grid", gap: 10 }}>
                                      {breakdownEntries.map(([groupKey, groupValue]) => {
                                        if (isPlainObject(groupValue)) {
                                          const groupEntries = Object.entries(groupValue);
                                          return (
                                            <div key={groupKey} style={{ display: "grid", gap: 6 }}>
                                              <div style={adminStyles.detailsText}>{groupKey}</div>
                                              <div style={adminStyles.wrappedList}>
                                                {groupEntries.length > 0 ? (
                                                  groupEntries.map(([itemKey, amount]) => (
                                                    <span
                                                      key={`${groupKey}-${itemKey}`}
                                                      style={adminStyles.wrappedListItem}
                                                    >
                                                      {formatBreakdownValue(groupKey, itemKey, amount)}
                                                    </span>
                                                  ))
                                                ) : (
                                                  <span style={adminStyles.detailsText}>—</span>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div key={groupKey} style={adminStyles.detailsText}>
                                            {groupKey}: {JSON.stringify(groupValue)}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div style={adminStyles.detailsText}>—</div>
                                  )}
                                </div>
                              </div>

                              <details>
                                <summary>Raw payload</summary>
                                <pre style={adminStyles.pre}>{JSON.stringify(payloadJson, null, 2)}</pre>
                              </details>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === "invites" ? (
        <section style={adminStyles.card}>
          <h3 style={{ margin: 0 }}>Create Invite</h3>
          {electionsError ? <div style={adminStyles.error}>{electionsError}</div> : null}

          <form style={{ display: "grid", gap: 12 }} onSubmit={onSubmitInvite}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13 }}>Email</span>
              <input
                type="email"
                required
                style={adminStyles.input}
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                disabled={inviteSubmitting || shouldShowForbidden}
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13 }}>area_id</span>
              <select
                style={adminStyles.select}
                value={selectedAreaId || ""}
                onChange={(event) => setSelectedAreaId(toPositiveIntOrNull(event.target.value))}
                disabled={inviteSubmitting || shouldShowForbidden}
              >
                {areaOptions.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name} ({area.id})
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13 }}>Role</span>
              <select
                style={adminStyles.select}
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value)}
                disabled={inviteSubmitting || shouldShowForbidden}
              >
                {INVITE_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 13 }}>enabled_elections</span>
              <select
                multiple
                size={Math.min(10, Math.max(4, electionsCatalog.length || 4))}
                style={{ ...adminStyles.select, minHeight: 110 }}
                value={enabledElectionIds}
                onChange={(event) => {
                  const values = Array.from(event.target.selectedOptions).map((opt) => opt.value);
                  setEnabledElectionIds(values);
                }}
                disabled={inviteSubmitting || shouldShowForbidden || electionsLoading}
              >
                {electionsCatalog.map((item) => (
                  <option key={String(item.id)} value={String(item.id)}>
                    {String(item.display_name || item.name || item.id)} ({String(item.id)})
                  </option>
                ))}
              </select>
              {electionsLoading ? (
                <span style={{ fontSize: 12, opacity: 0.8 }}>Loading elections...</span>
              ) : null}
            </label>

            <fieldset style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 10 }}>
              <legend style={{ fontSize: 13 }}>modules</legend>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {MODULE_OPTIONS.map((module) => (
                  <label key={module.key} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={Boolean(modulesMap[module.key])}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setModulesMap((prev) => ({
                          ...prev,
                          [module.key]: checked,
                        }));
                      }}
                      disabled={inviteSubmitting || shouldShowForbidden}
                    />
                    <span>{module.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label style={{ display: "grid", gap: 6, maxWidth: 220 }}>
              <span style={{ fontSize: 13 }}>expires_in_days</span>
              <input
                type="number"
                min={1}
                max={30}
                step={1}
                style={adminStyles.input}
                value={expiresInDays}
                onChange={(event) => setExpiresInDays(event.target.value)}
                disabled={inviteSubmitting || shouldShowForbidden}
              />
            </label>

            {inviteError ? <div style={adminStyles.error}>{inviteError}</div> : null}
            <button
              type="submit"
              style={adminStyles.primaryBtn}
              disabled={inviteSubmitting || shouldShowForbidden || !selectedAreaId}
            >
              {inviteSubmitting ? "Creating..." : "Create invite"}
            </button>
          </form>

          {inviteResult ? (
            <div style={{ display: "grid", gap: 8 }}>
              <div style={adminStyles.warning}>Token is shown only once. Store it securely now.</div>
              <div style={adminStyles.message}>
                <div style={{ marginBottom: 8 }}>
                  <strong>token:</strong>
                  <div style={{ marginTop: 4, wordBreak: "break-all", fontFamily: "monospace", fontSize: 12 }}>
                    {String(inviteResult.token || "")}
                  </div>
                  <button
                    type="button"
                    style={{ ...adminStyles.button, marginTop: 6 }}
                    onClick={() => onCopy(inviteResult.token, "Token")}
                  >
                    Copy token
                  </button>
                </div>
                <div>
                  <strong>activate_url:</strong>
                  <div style={{ marginTop: 4, wordBreak: "break-all", fontFamily: "monospace", fontSize: 12 }}>
                    {inviteActivateUrl || String(inviteResult.activate_url || "")}
                  </div>
                  <button
                    type="button"
                    style={{ ...adminStyles.button, marginTop: 6 }}
                    onClick={() => onCopy(inviteActivateUrl || inviteResult.activate_url, "Activate URL")}
                  >
                    Copy activate_url
                  </button>
                </div>
              </div>
              {copyMessage ? <div style={{ fontSize: 12, opacity: 0.8 }}>{copyMessage}</div> : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
